import React, { useMemo } from 'react'
import { ArrowRight, DollarSign } from 'lucide-react'
import { useCRMStore } from '../store/crmStore'

export default function SalesPipelinePage() {
  const opportunities = useCRMStore((state) => state.opportunities)
  const pipelineStages = useCRMStore((state) => state.pipelineStages)

  const stages = ['Prospect', 'Negotiation', 'Proposal', 'Closed Won']

  const opportunitiesByStage = useMemo(() => {
    const byStage = {}
    stages.forEach(stage => {
      byStage[stage] = opportunities.filter(o => o.stage === stage)
    })
    return byStage
  }, [opportunities])

  const stageStats = useMemo(() => {
    const stats = {}
    stages.forEach(stage => {
      const opps = opportunitiesByStage[stage]
      stats[stage] = {
        count: opps.length,
        value: opps.reduce((sum, o) => sum + o.value, 0),
        avgProbability: opps.length > 0 ? Math.round(opps.reduce((sum, o) => sum + o.probability, 0) / opps.length) : 0,
      }
    })
    return stats
  }, [opportunitiesByStage])

  const totalPipelineValue = useMemo(() => {
    return opportunities.reduce((sum, o) => sum + o.value, 0)
  }, [opportunities])

  const getStageColor = (stage) => {
    switch (stage) {
      case 'Prospect': return 'from-blue-600 to-blue-700 border-blue-400'
      case 'Negotiation': return 'from-purple-600 to-purple-700 border-purple-400'
      case 'Proposal': return 'from-yellow-600 to-yellow-700 border-yellow-400'
      case 'Closed Won': return 'from-green-600 to-green-700 border-green-400'
      default: return 'from-slate-600 to-slate-700 border-slate-400'
    }
  }

  const getProgressColor = (probability) => {
    if (probability >= 75) return 'from-green-500 to-green-600'
    if (probability >= 50) return 'from-yellow-500 to-yellow-600'
    if (probability >= 25) return 'from-orange-500 to-orange-600'
    return 'from-red-500 to-red-600'
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-white mb-2">Sales Pipeline</h1>
            <p className="text-slate-400">Track opportunities through your sales stages</p>
          </div>

          {/* Overall Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-8">
              <p className="text-slate-400 text-sm mb-2">Total Pipeline Value</p>
              <p className="text-4xl font-bold text-blue-400 mb-2">${(totalPipelineValue / 1000).toFixed(0)}K</p>
              <p className="text-sm text-slate-500">{opportunities.length} opportunities</p>
            </div>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-8">
              <p className="text-slate-400 text-sm mb-2">Average Deal Size</p>
              <p className="text-4xl font-bold text-green-400 mb-2">
                ${opportunities.length > 0 ? (totalPipelineValue / opportunities.length / 1000).toFixed(0) : 0}K
              </p>
              <p className="text-sm text-slate-500">Across all stages</p>
            </div>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-8">
              <p className="text-slate-400 text-sm mb-2">Weighted Forecast</p>
              <p className="text-4xl font-bold text-yellow-400 mb-2">
                ${opportunities.reduce((sum, o) => sum + (o.value * o.probability / 100), 0) / 1000 | 0}K
              </p>
              <p className="text-sm text-slate-500">Probability-adjusted</p>
            </div>
          </div>

          {/* Pipeline Stages */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-12">
            {stages.map((stage, index) => (
              <React.Fragment key={stage}>
                <div>
                  <div className={`bg-gradient-to-br ${getStageColor(stage)} rounded-lg p-6 border-2 mb-4 text-white`}>
                    <h3 className="text-lg font-bold mb-4">{stage}</h3>

                    <div className="space-y-3 mb-4">
                      <div>
                        <p className="text-sm opacity-90">Count</p>
                        <p className="text-2xl font-bold">{stageStats[stage].count}</p>
                      </div>
                      <div>
                        <p className="text-sm opacity-90">Value</p>
                        <p className="text-2xl font-bold">${(stageStats[stage].value / 1000).toFixed(0)}K</p>
                      </div>
                      <div>
                        <p className="text-sm opacity-90">Avg Probability</p>
                        <p className="text-2xl font-bold">{stageStats[stage].avgProbability}%</p>
                      </div>
                    </div>
                  </div>

                  {/* Opportunities in this stage */}
                  <div className="space-y-3">
                    {opportunitiesByStage[stage].map((opp) => (
                      <div
                        key={opp.id}
                        className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/20 p-4 hover:border-blue-400/40 transition-all"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-semibold text-white text-sm flex-1">{opp.title}</h4>
                          <span className="text-xs text-blue-300 font-bold">${(opp.value / 1000).toFixed(0)}K</span>
                        </div>

                        <div className="mb-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-slate-400">Probability</span>
                            <span className="text-xs text-slate-300 font-semibold">{opp.probability}%</span>
                          </div>
                          <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div
                              className={`h-full bg-gradient-to-r ${getProgressColor(opp.probability)}`}
                              style={{ width: `${opp.probability}%` }}
                            ></div>
                          </div>
                        </div>

                        <div className="text-xs text-slate-500">
                          <p>Close: {new Date(opp.closeDate).toLocaleDateString()}</p>
                        </div>
                      </div>
                    ))}
                    {opportunitiesByStage[stage].length === 0 && (
                      <div className="bg-slate-800/50 rounded-lg border border-slate-700/30 p-4 text-center">
                        <p className="text-sm text-slate-500">No opportunities</p>
                      </div>
                    )}
                  </div>
                </div>

                {index < stages.length - 1 && (
                  <div className="hidden lg:flex items-center justify-center">
                    <ArrowRight size={32} className="text-slate-600" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Pipeline Details Table */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 overflow-hidden">
            <div className="p-6 border-b border-blue-500/20">
              <h3 className="text-xl font-bold text-white">All Opportunities</h3>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-blue-500/20 bg-slate-900/50">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Title</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Stage</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Value</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Probability</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Weighted Value</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Close Date</th>
                  </tr>
                </thead>
                <tbody>
                  {opportunities.map((opp) => {
                    const weighted = opp.value * opp.probability / 100
                    return (
                      <tr key={opp.id} className="border-b border-blue-500/10 hover:bg-slate-800/50 transition-colors">
                        <td className="px-6 py-4 text-white font-medium">{opp.title}</td>
                        <td className="px-6 py-4">
                          <span className={`text-xs px-3 py-1 rounded-full font-semibold bg-${opp.stage === 'Proposal' ? 'yellow' : opp.stage === 'Negotiation' ? 'purple' : opp.stage === 'Closed Won' ? 'green' : 'blue'}-500/20 text-${opp.stage === 'Proposal' ? 'yellow' : opp.stage === 'Negotiation' ? 'purple' : opp.stage === 'Closed Won' ? 'green' : 'blue'}-300`}>
                            {opp.stage}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-green-400 font-semibold">${(opp.value / 1000).toFixed(0)}K</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-2 bg-slate-700 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-blue-500"
                                style={{ width: `${opp.probability}%` }}
                              ></div>
                            </div>
                            <span className="text-sm text-blue-300 font-semibold">{opp.probability}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-yellow-400 font-semibold">${(weighted / 1000).toFixed(0)}K</td>
                        <td className="px-6 py-4 text-slate-400">{new Date(opp.closeDate).toLocaleDateString()}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
  )
}
